import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NewLeadDialog } from "@/components/NewLeadDialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Users, KanbanSquare, CalendarClock, Trophy,
  BarChart3, Settings, Plus, LogOut, Menu, Building2, Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare },
  { to: "/followups", label: "Follow-ups", icon: CalendarClock },
  { to: "/kunden", label: "Kunden", icon: Trophy },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/einstellungen", label: "Einstellungen", icon: Settings },
];

const FUTURE = ["Angebote", "Projekte", "Onboarding", "Rechnungen"];

function SidebarContent({ onNavigate }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Building2 className="h-5 w-5" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-sm">WebDesign Damerow</div>
          <div className="text-[11px] text-muted-foreground">Sales CRM</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} onClick={onNavigate}
            data-testid={`nav-${item.to.slice(1)}`}
            className={({ isActive }) => cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
            )}>
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        ))}
        <div className="pt-4 mt-2 border-t border-border">
          <div className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Bald verfügbar
          </div>
          {FUTURE.map((f) => (
            <div key={f} className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground/50">
              <Lock className="h-[15px] w-[15px]" />
              {f}
            </div>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 border-r border-border bg-card">
        <SidebarContent />
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 flex-shrink-0 items-center justify-between gap-3 border-b border-border bg-card/60 backdrop-blur px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-card">
                <SidebarContent onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <span className="hidden sm:block text-sm text-muted-foreground">
              Angemeldet als <span className="text-foreground font-medium">{user?.name || user?.email}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="header-new-lead-btn" onClick={() => setNewLeadOpen(true)} className="gap-1.5">
              <Plus className="h-4 w-4" /> <span className="hidden xs:inline sm:inline">Neuer Lead</span>
            </Button>
            <Button variant="ghost" size="icon" data-testid="logout-btn" onClick={handleLogout} title="Abmelden">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      <NewLeadDialog open={newLeadOpen} onOpenChange={setNewLeadOpen}
        onCreated={(lead) => navigate(`/leads/${lead.id}`)} />
    </div>
  );
}
